import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Mail, Palmtree } from 'lucide-react';
import Button from '../components/Button';
import { useToast } from '../contexts/ToastContext';
import { supabase } from '../lib/supabase';
import './Login.css';

const forgotSchema = z.object({
    email: z.string().email('Digite um email válido'),
});

type ForgotFormInputs = z.infer<typeof forgotSchema>;

export default function ForgotPassword() {
    const toast = useToast();
    const [sent, setSent] = useState(false);
    const [loading, setLoading] = useState(false);

    const { register, handleSubmit, formState: { errors } } = useForm<ForgotFormInputs>({
        resolver: zodResolver(forgotSchema),
        defaultValues: { email: '' },
    });

    const onSubmit = async (data: ForgotFormInputs) => {
        setLoading(true);
        try {
            await supabase.functions.invoke('send-reset-email', {
                body: { email: data.email },
            });
            setSent(true); // sempre mostra sucesso
        } catch {
            toast.error('Não foi possível enviar. Tente novamente.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="login-page">
            <div className="login-background">
                <div className="bg-shape shape-1"></div>
                <div className="bg-shape shape-2"></div>
                <div className="bg-shape shape-3"></div>
            </div>

            <div className="login-container">
                <div className="login-card">
                    <div className="login-header">
                        <div className="logo-icon">
                            <Palmtree size={32} />
                        </div>
                        <h1>Esqueceu a senha?</h1>
                        <p>Informe seu email e enviaremos um link para redefinir a senha.</p>
                    </div>

                    {sent ? (
                        <>
                            <div className="auth-notice">
                                Se o email existir em nossa base, você receberá um link em breve.
                            </div>
                            <div className="login-footer">
                                <p><Link to="/login">Voltar ao login</Link></p>
                            </div>
                        </>
                    ) : (
                        <form onSubmit={handleSubmit(onSubmit)} className="login-form">
                            <div className="input-group">
                                <div className="input-icon">
                                    <Mail size={20} />
                                </div>
                                <input
                                    type="email"
                                    placeholder="Email"
                                    autoComplete="email"
                                    className={errors.email ? 'error' : ''}
                                    {...register('email')}
                                />
                            </div>
                            {errors.email && <span className="field-error">{errors.email.message}</span>}

                            <Button
                                type="submit"
                                variant="primary"
                                fullWidth
                                disabled={loading}
                                isLoading={loading}
                                className="login-btn"
                            >
                                {loading ? 'Enviando...' : 'Enviar link'}
                            </Button>
                        </form>
                    )}

                    {!sent && (
                        <div className="login-footer">
                            <p><Link to="/login">Voltar ao login</Link></p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
